import { apiAnalytics } from "@/api/analytics/analytics.api.ts";
import { filtersLibrary } from "@/components/filter/filter.types.ts";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import { Search } from "@/components/search/Search.tsx";
import { TimeOffHistoryReportTable } from "@/components/time-off/components/TimeOffHistoryReportTable.tsx";
import { useDownloadHelper } from "@/hooks/use-download.helper.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import useTimeOffHook from "@/hooks/use-time-off.hook.tsx";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { useTimeOffAnalyticsHistory } from "@/queries/analytics/use-time-off-analytics.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiAnalyticsTimeOffLeaveUsedFilter } from "@/types/analytics/analytics.time-off.types.ts";
import { AnalyticReportTypes, ApiExportFormat } from "@/types/analytics/analytics.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { ApiTimeOffLogType } from "@/types/time-off/time-off.types.ts";
import {
  Button,
  CoreFilterType,
  DropdownMenu,
  DropdownMenuItem,
  FilterRenderType,
  FilterValueType,
  FontAwesomeIcon,
  Page,
  PaginationBlock,
  Stack,
} from "@skailer-com/uikit";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useDebounce, useEffectOnce } from "react-use";

export const TimeOffHistoryReport = () => {
  const { t } = useTranslation();
  const [, setSearchParams] = useSearchParams();
  const { getActionTypeTitle } = useTimeOffHook();
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
  const { canIEdit } = usePermissions();

  useTitle(t("reporting:time_off_history_report_title"));

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.reporting.viewed, {
      [analyticProperties.title]: AnalyticReportTypes.timeOffHistory,
    });
  });

  const library = filtersLibrary();
  const {
    filter: { filter: filter },
    filterButton,
    isFilterReady,
  } = useAppFilter<ApiAnalyticsTimeOffLeaveUsedFilter>({
    filters: [
      library.page_number,
      library.text,
      library.category_ids,
      library.date_from,
      library.date_to,
      library.request_statuses,
      library.employee_ids,
      library.job_profile_ids,
      library.job_role_ids,
      library.department_ids,
      library.division_ids,
      library.location_ids,
      library.employee_lifecycle_statuses,
      {
        name: "action_types",
        type: CoreFilterType.urlParam,
        renderType: FilterRenderType.multiselect,
        valueType: FilterValueType.stringArray,
        label: t("common:type"),
        options: [
          ApiTimeOffLogType.accrual,
          ApiTimeOffLogType.used,
          ApiTimeOffLogType.canceled,
          ApiTimeOffLogType.adjustment,
          ApiTimeOffLogType.carryover,
          ApiTimeOffLogType.initial_balance,
          ApiTimeOffLogType.balance_import,
        ].map((x) => ({
          id: x,
          title: getActionTypeTitle(x),
        })),
      },
    ],
    source: AnalyticReportTypes.timeOffHistory,
  });

  const { data: historyReport, isLoading: isLoadingHistory } = useTimeOffAnalyticsHistory({
    filter: filter,
    enabled: isFilterReady,
  });

  useDebounce(
    () => {
      setSearchParams((searchParams) => {
        if (searchTerm) searchParams.set("search", searchTerm);
        else searchParams.delete("search");
        searchParams.set("page", "1");
        return searchParams;
      });
    },
    300,
    [searchTerm]
  );

  const { isExporting, downloadFile } = useDownloadHelper();
  const handleExportClick = useCallback(
    (format: ApiExportFormat) => {
      const filterToExport: ApiAnalyticsTimeOffLeaveUsedFilter = {
        ...filter,
        format: format,
      };

      downloadFile({
        apiCall: () => apiAnalytics.timeOff.historyReportExport(filterToExport),
        onSuccess: () => {
          analyticsService.trackEvent(analyticEvents.dataExported, {
            [analyticProperties.title]: AnalyticReportTypes.timeOffHistory,
          });
        },
      });
    },
    [filter]
  );

  return (
    <>
      <Page className={"h-full"}>
        <Page.Header title={t("time_off:all_requests_history")}>
          <Stack direction={"horizontal"} gap={"sm"} className={"overflow-hidden"}>
            {canIEdit(ApiPermission.reporting_export) && (
              <DropdownMenu
                trigger={
                  <Button isLoading={isExporting} variant={"secondary"}>
                    {`${t("common:export")}...`}
                  </Button>
                }
              >
                <DropdownMenuItem
                  onClick={() => handleExportClick(ApiExportFormat.xlsx)}
                  leftIcon={<FontAwesomeIcon icon="fa-light fa-file-xls" />}
                  title={t("common:export_to_xlsx")}
                />
              </DropdownMenu>
            )}
            {filterButton()}
          </Stack>
        </Page.Header>

        <Page.Content stretch>
          <Page.StickyLayout
            toolbar={
              <Search
                onInput={(e) => {
                  setSearchTerm(e.currentTarget.value);
                }}
                type={"text"}
                value={searchTerm}
                placeholder={t("common:search")}
                className={"w-96"}
              />
            }
            footer={
              historyReport ? (
                <PaginationBlock
                  metadata={historyReport.config.metadata}
                  onPageChange={(page) => {
                    setSearchParams((searchParams) => {
                      searchParams.set("page", page.toString());
                      return searchParams;
                    });
                  }}
                />
              ) : undefined
            }
          >
            <TimeOffHistoryReportTable
              logs={historyReport?.report.rows || []}
              isLoading={isLoadingHistory}
            />
          </Page.StickyLayout>
        </Page.Content>
      </Page>
    </>
  );
};
